// =============================================================================
// FILE:
// src/modules/pms/task_material/components/TaskMaterialGrid.js
// =============================================================================

import React, { useState } from "react";

import {
  updateTaskMaterialQty,
  deleteTaskMaterial
} from "../../services/taskMaterialService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function TaskMaterialGrid({

  rows = [],

  loading = false,

  onRefresh

}) {

  // ===========================================================================
  // STATE
  // ===========================================================================
  const [savingId, setSavingId] = useState(null);

  // ===========================================================================
  // UPDATE QTY
  // ===========================================================================
  const handleUpdateQty = async (
    row,
    value
  ) => {

    try {

      setSavingId(row.id);

      await updateTaskMaterialQty(
        row.id,
        {
          planned_qty: value,
          remarks: row.remarks
        }
      );

      if (onRefresh) {
        await onRefresh();
      }

    } catch (err) {

      alert(
        err?.response?.data?.message ||
        "Failed to update quantity"
      );

    } finally {

      setSavingId(null);
    }
  };

  // ===========================================================================
  // DELETE
  // ===========================================================================
  const handleDelete = async (row) => {

    const confirmed = window.confirm(
      `Delete material "${row.material_description}" ?`
    );

    if (!confirmed) {
      return;
    }

    try {

      await deleteTaskMaterial(row.id);

      if (onRefresh) {
        await onRefresh();
      }

    } catch (err) {

      alert(
        err?.response?.data?.message ||
        "Failed to delete material"
      );
    }
  };

  // ===========================================================================
  // LOADING
  // ===========================================================================
  if (loading) {

    return (
      <div style={styles.loader}>
        Loading materials...
      </div>
    );
  }

  // ===========================================================================
  // EMPTY
  // ===========================================================================
  if (!rows || rows.length === 0) {

    return (
      <div style={styles.emptyState}>
        No materials allocated yet
      </div>
    );
  }

  // ===========================================================================
  // MAIN RENDER
  // ===========================================================================
  return (

    <div style={styles.container}>

      {/* ===================================================================== */}
      {/* HEADER */}
      {/* ===================================================================== */}
      <div style={styles.headerRow}>

        <div style={styles.colMaterial}>
          Material
        </div>

        <div style={styles.colQty}>
          BOM Qty
        </div>

        <div style={styles.colQty}>
          Planned Qty
        </div>

        <div style={styles.colQty}>
          Reserved
        </div>

        <div style={styles.colQty}>
          Issued
        </div>

        <div style={styles.colQty}>
          Consumed
        </div>

        <div style={styles.colStatus}>
          Status
        </div>

        <div style={styles.colAction}>
          Action
        </div>

      </div>

      {/* ===================================================================== */}
      {/* BODY */}
      {/* ===================================================================== */}
      <div style={styles.body}>

        {rows.map((row) => (

          <div
            key={row.id}
            style={styles.dataRow}
          >

            {/* --------------------------------------------------------------- */}
            {/* MATERIAL */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colMaterial}>

              <div style={styles.materialTitle}>

              {row.material_code || "-"}

              {" - "}

              {row.material_name || "-"}

            </div>

            <div style={styles.materialSubText}>

              UOM:
              {" "}

              {row.uom_code || "-"}

            </div>

            </div>

            {/* --------------------------------------------------------------- */}
            {/* BOM QTY */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colQty}>
              {row.bom_qty || 0}
            </div>

            {/* --------------------------------------------------------------- */}
            {/* PLANNED QTY */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colQty}>

              <input
                type="number"
                value={row.planned_qty || 0}

                disabled={savingId === row.id}

                onChange={(e) =>
                  handleUpdateQty(
                    row,
                    e.target.value
                  )
                }

                style={styles.qtyInput}
              />

            </div>

            {/* --------------------------------------------------------------- */}
            {/* RESERVED */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colQty}>
              {row.reserved_qty || 0}
            </div>

            {/* --------------------------------------------------------------- */}
            {/* ISSUED */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colQty}>
              {row.issued_qty || 0}
            </div>

            {/* --------------------------------------------------------------- */}
            {/* CONSUMED */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colQty}>
              {row.consumed_qty || 0}
            </div>

            {/* --------------------------------------------------------------- */}
            {/* STATUS */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colStatus}>

              <span style={getStatusStyle(
                row.status_code
              )}>
                {row.status_code}
              </span>

            </div>

            {/* --------------------------------------------------------------- */}
            {/* ACTION */}
            {/* --------------------------------------------------------------- */}
            <div style={styles.colAction}>

              <button
                style={styles.deleteBtn}
                onClick={() =>
                  handleDelete(row)
                }
              >
                ✖
              </button>

            </div>

          </div>

        ))}

      </div>

    </div>
  );
}


// =============================================================================
// STATUS STYLE
// =============================================================================

function getStatusStyle(status) {

  switch (status) {

    case "CONSUMED":
      return styles.statusGreen;

    case "ISSUED":
      return styles.statusBlue;

    default:
      return styles.statusGray;
  }
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%"
  },

  headerRow: {
    display: "flex",
    padding: "12px",
    borderBottom: "1px solid #ddd",
    background: "#f9fafb",
    fontWeight: 600,
    fontSize: 13
  },

  body: {
    flex: 1,
    overflowY: "auto"
  },

  dataRow: {
    display: "flex",
    padding: "12px",
    borderBottom: "1px solid #f1f5f9",
    alignItems: "center"
  },

  colMaterial: {
    flex: 3
  },

  colQty: {
    flex: 1,
    textAlign: "center"
  },

  colStatus: {
    flex: 1.5,
    textAlign: "center"
  },

  colAction: {
    flex: 1,
    textAlign: "center"
  },

  materialTitle: {
    fontWeight: 600,
    fontSize: 14
  },

  materialSubText: {
    fontSize: 11,
    color: "#6b7280",
    marginTop: 4
  },

  qtyInput: {
    width: 80,
    padding: "6px 8px",
    borderRadius: 6,
    border: "1px solid #ccc",
    textAlign: "center"
  },

  deleteBtn: {
    border: "none",
    background: "#fee2e2",
    width: 30,
    height: 30,
    borderRadius: 6,
    cursor: "pointer"
  },

  loader: {
    padding: 20,
    textAlign: "center"
  },

  emptyState: {
    padding: 30,
    textAlign: "center",
    color: "#6b7280"
  },

  statusGray: {
    background: "#f3f4f6",
    color: "#374151",
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 11
  },

  statusBlue: {
    background: "#dbeafe",
    color: "#1d4ed8",
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 11
  },

  statusGreen: {
    background: "#dcfce7",
    color: "#15803d",
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 11
  }
};